import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";

export interface TransactionAttachment {
  id: string;
  transaction_id: string;
  organization_id: string | null;
  user_id: string;
  file_path: string;
  file_name: string;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string;
}

const BUCKET = "comprovantes";
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const ACCEPTED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

const sanitizeFileName = (name: string) =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .slice(-80);

export function useTransactionAttachments() {
  const { user } = useAuth();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);

  const refreshCounts = useCallback(async () => {
    if (!user) {
      setCounts({});
      return;
    }
    try {
      const { data, error } = await supabase
        .from("transaction_attachments")
        .select("transaction_id");
      if (error) throw error;
      const map: Record<string, number> = {};
      (data || []).forEach((row: { transaction_id: string }) => {
        map[row.transaction_id] = (map[row.transaction_id] || 0) + 1;
      });
      setCounts(map);
    } catch (e) {
      logger.error("[attachments] refreshCounts", e);
    }
  }, [user]);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  const listAttachments = useCallback(async (transactionId: string): Promise<TransactionAttachment[]> => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("transaction_attachments")
        .select("*")
        .eq("transaction_id", transactionId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as TransactionAttachment[];
    } catch (e) {
      logger.error("[attachments] list", e);
      toast({ title: "Erro ao carregar comprovantes", variant: "destructive" });
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const uploadAttachments = useCallback(
    async (transactionId: string, files: File[]): Promise<number> => {
      if (!user) return 0;

      // Organização do usuário define a pasta raiz (regra de acesso do storage)
      const { data: orgId, error: orgError } = await supabase.rpc("get_user_organization_id", {
        p_user_id: user.id,
      });
      if (orgError || !orgId) {
        toast({ title: "Não foi possível identificar sua organização", variant: "destructive" });
        return 0;
      }

      let uploaded = 0;
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE) {
          toast({
            title: `"${file.name}" excede 10 MB`,
            description: "Envie um arquivo menor.",
            variant: "destructive",
          });
          continue;
        }
        if (file.type && !ACCEPTED_TYPES.includes(file.type)) {
          toast({
            title: `Formato não suportado: ${file.name}`,
            description: "Aceitamos PDF, JPG, PNG e WEBP.",
            variant: "destructive",
          });
          continue;
        }

        const path = `${orgId}/${transactionId}/${crypto.randomUUID()}-${sanitizeFileName(file.name)}`;
        const { error: uploadError } = await supabase.storage
          .from(BUCKET)
          .upload(path, file, { contentType: file.type || undefined, upsert: false });

        if (uploadError) {
          logger.error("[attachments] upload", uploadError);
          toast({ title: `Falha ao enviar ${file.name}`, variant: "destructive" });
          continue;
        }

        const { error: insertError } = await supabase.from("transaction_attachments").insert({
          transaction_id: transactionId,
          user_id: user.id,
          organization_id: orgId as string,
          file_path: path,
          file_name: file.name,
          mime_type: file.type || null,
          size_bytes: file.size,
        });

        if (insertError) {
          logger.error("[attachments] insert", insertError);
          await supabase.storage.from(BUCKET).remove([path]);
          toast({
            title: `Falha ao registrar ${file.name}`,
            description: "Verifique se você tem permissão para editar lançamentos.",
            variant: "destructive",
          });
          continue;
        }
        uploaded++;
      }

      if (uploaded > 0) {
        toast({ title: `${uploaded} comprovante(s) anexado(s)` });
        await refreshCounts();
      }
      return uploaded;
    },
    [user, refreshCounts]
  );

  const removeAttachment = useCallback(
    async (attachment: TransactionAttachment): Promise<boolean> => {
      try {
        const { error } = await supabase
          .from("transaction_attachments")
          .delete()
          .eq("id", attachment.id);
        if (error) throw error;
        await supabase.storage.from(BUCKET).remove([attachment.file_path]);
        toast({ title: "Comprovante excluído" });
        await refreshCounts();
        return true;
      } catch (e) {
        logger.error("[attachments] remove", e);
        toast({ title: "Erro ao excluir comprovante", variant: "destructive" });
        return false;
      }
    },
    [refreshCounts]
  );

  const openAttachment = useCallback(async (attachment: TransactionAttachment, download = false) => {
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(attachment.file_path, 60 * 10, download ? { download: attachment.file_name } : undefined);
      if (error || !data?.signedUrl) throw error;
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      logger.error("[attachments] signedUrl", e);
      toast({ title: "Não foi possível abrir o comprovante", variant: "destructive" });
    }
  }, []);

  return {
    counts,
    loading,
    refreshCounts,
    listAttachments,
    uploadAttachments,
    removeAttachment,
    openAttachment,
  };
}
